import { fetchApi, shouldAttachBearerAuthorization } from '@codametrix/client-api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Spinner, Text } from '@chakra-ui/react';
import { ApolloClient } from '@apollo/client';
import { QueryGroupUserRoles, QueryWorkers } from './queries';
import { Collection, CollectionWithWorkItemCounts, WorkitemStatus } from './types';
import { categorizeProjects } from './utils';
import { fetchProjectsWithCounts } from './fetchProjectsWithCounts';
import {
  getUserRolesFromActiveContext,
  createProjectItemFromQaRule,
  filterProject,
  fetchQaRules,
} from './projectUtils';

// ─── Constants ────────────────────────────────────────────────────────────────

const TEXT_PRIMARY = '#FFFFFF';
const TEXT_SELECTED = '#E5927D';
const SELECTED_BG = 'rgba(229, 146, 125, 0.15)';
const HOVER_BG = 'rgba(255, 255, 255, 0.04)';
const TEXT_LOW = '#8CB4C2';
const QA_EXPANDED_SECTION_KEY = 'projectSelectColumn_expandedSection';
const REFRESH_INTERVAL_MS = 30000;

const WORK_ITEMS_UPDATED_EVENT = 'workitems-updated';
const QA_RULE_UPDATED_EVENT = 'qa-rule-updated';

const SERVICE_LINE_MAPPER: Record<string, string> = {
  'Bedside Pro': 'IPC',
  Surgery: 'SURGERY',
  Radiology: 'RADIOLOGY',
};

// ─── Component ────────────────────────────────────────────────────────────────

interface QANavigationContentProps {
  apolloClient: ApolloClient<any>;
  userContext: any;
  onProjectSelected: (project: Collection, status?: WorkitemStatus | null) => void;
  selectedProjectId: string | null;
  searchTerm: string;
  isManager?: boolean;
  onHomeClick?: () => void;
}

const QANavigationContent: React.FC<QANavigationContentProps> = ({
  apolloClient,
  userContext,
  onProjectSelected,
  selectedProjectId,
  searchTerm,
  isManager = false,
  onHomeClick,
}) => {
  const authToken = useSelector((state: CMx.ApplicationState) => state.login.auth?.token);

  const [projectList, setProjectList] = useState<{
    active: CollectionWithWorkItemCounts[];
    forReview: CollectionWithWorkItemCounts[];
    archived: CollectionWithWorkItemCounts[];
  }>({ active: [], forReview: [], archived: [] });

  const [isLoading, setIsLoading] = useState(true);
  const [countIsLoading, setCountIsLoading] = useState(false);
  const [isActiveExpanded, setIsActiveExpanded] = useState(true);
  const [isForReviewExpanded, setIsForReviewExpanded] = useState(false);
  const [isArchivedExpanded, setIsArchivedExpanded] = useState(false);

  // Lifted to component scope so polling/events can reference them
  const [workerId, setWorkerId] = useState('');
  const [activeContextRoles, setActiveContextRoles] = useState<string[]>([]);
  const [dbUserRoles, setDbUserRoles] = useState<any[]>([]);

  // Rubric info cache — ref for stale-closure-free reads, state to trigger re-renders
  const rubricInfoCacheRef = useRef<Map<number, { name: string; version: string }>>(new Map());
  const [rubricInfoCache, setRubricInfoCache] = useState<Map<number, { name: string; version: string }>>(new Map());

  // Mirror projectList into a ref so polling closures always see current list
  const projectListRef = useRef(projectList);
  useEffect(() => { projectListRef.current = projectList; }, [projectList]);

  // Track if auto-select has fired so we don't repeat it
  const didAutoSelect = useRef(false);

  // ─── Section state restore ─────────────────────────────────────────────────

  useEffect(() => {
    const saved = sessionStorage.getItem(QA_EXPANDED_SECTION_KEY);
    if (saved === 'forReview') {
      setIsActiveExpanded(false);
      setIsForReviewExpanded(true);
    } else if (saved === 'archived') {
      setIsActiveExpanded(false);
      setIsArchivedExpanded(true);
    }
  }, []);

  // ─── Count fetch ──────────────────────────────────────────────────────────

  const getProjectWorkItemCounts = useCallback(async (
    collections: CollectionWithWorkItemCounts[],
    isPolling: boolean,
    roles: string[],
    wId: string,
  ) => {
    if (!isPolling) setCountIsLoading(true);
    const ids = collections.map(c => c.collection_id!).filter(Boolean);
    if (ids.length === 0) {
      if (!isPolling) setCountIsLoading(false);
      return;
    }
    try {
      const withCounts = await fetchProjectsWithCounts(ids, collections, roles, wId, apolloClient, isPolling);
      setProjectList(prev => {
        const map = new Map<string, CollectionWithWorkItemCounts>();
        [...prev.active, ...prev.forReview, ...prev.archived].forEach(p => map.set(p.collection_id!, p));
        withCounts.forEach(p => map.set(p.collection_id!, p));
        return categorizeProjects(Array.from(map.values()));
      });
    } catch (err) {
      console.error('Error fetching work item counts:', err);
    } finally {
      if (!isPolling) setCountIsLoading(false);
    }
  }, [apolloClient]);

  // ─── Rubric info fetch ────────────────────────────────────────────────────

  const fetchRubricInfo = useCallback(async (projects: CollectionWithWorkItemCounts[]) => {
    const uniqueIds = [
      ...new Set(
        projects
          .map(p => p.rubricVersionId)
          .filter((id): id is number => id != null),
      ),
    ].filter(id => !rubricInfoCacheRef.current.has(id));

    if (uniqueIds.length === 0) return;
    if (shouldAttachBearerAuthorization() && !authToken) return;

    const headers: Record<string, string> = {};
    if (shouldAttachBearerAuthorization() && authToken) {
      headers.Authorization = authToken.startsWith('Bearer')
        ? authToken
        : `Bearer ${authToken}`;
    }

    await Promise.all(
      uniqueIds.map(async id => {
        try {
          const resp = await fetchApi(`/api/cqa-service/v1/rubric-versions/${id}/full`, {
            headers,
          });
          if (!resp.ok) {
            console.error(`Failed to fetch rubric version ${id}: ${resp.status}`);
            return;
          }
          const data = await resp.json();
          rubricInfoCacheRef.current.set(id, {
            name: data?.rubric?.name ?? 'Unknown',
            version: `${data?.version?.majorVersion ?? '0'}.${data?.version?.minorVersion ?? '0'}`,
          });
        } catch (err) {
          console.error(`Failed to fetch rubric version ${id}:`, err);
        }
      }),
    );
    setRubricInfoCache(new Map(rubricInfoCacheRef.current));
  }, [authToken]);

  // ─── Initial load ─────────────────────────────────────────────────────────

  const loadProjects = useCallback(async (
    roles: string[],
    dbUserRoles: any[],
    wId: string,
    userCtx: any,
  ) => {
    const serviceLine =
      SERVICE_LINE_MAPPER[userCtx?.serviceLine?.name] ??
      userCtx?.serviceLine?.name ??
      '';

    if (!serviceLine) return;

    const rules = await fetchQaRules(apolloClient, serviceLine);

    const filtered = rules
      .filter(rule => filterProject(rule, roles, dbUserRoles, wId))
      .map(rule => createProjectItemFromQaRule(rule, roles, wId));

    const categorized = categorizeProjects(filtered);
    setProjectList(categorized);
    setIsLoading(false);

    // Managers land on ManagerHome when no project is selected — skip auto-select
    if (!isManager && !selectedProjectId && !didAutoSelect.current) {
      const first =
        categorized.active[0] ?? categorized.forReview[0] ?? categorized.archived[0];
      if (first) {
        didAutoSelect.current = true;
        onProjectSelected(first, undefined);
      }
    }

    const refreshable = filtered.filter(p => p.availability !== false);
    if (refreshable.length > 0) {
      getProjectWorkItemCounts(refreshable, false, roles, wId);
    }
  }, [apolloClient, selectedProjectId, onProjectSelected, getProjectWorkItemCounts]);

  // ─── Bootstrap: fetch user roles + workerId, then load projects ───────────

  useEffect(() => {
    if (!userContext?.user?.userId) return;

    setIsLoading(true);

    Promise.all([
      apolloClient.mutate({
        mutation: QueryGroupUserRoles,
        variables: {
          groupUserRolesQuery: {
            query: `Select * from group_user_roles where user_id='${userContext.user.userId}' and status='ACTIVE';`,
          },
        },
      }),
      apolloClient.mutate({
        mutation: QueryWorkers,
        variables: {
          workerQuery: {
            query: `Select * from worker where user_id='${userContext.user.userId}' and subtenant_id = '${userContext.activeContext?.tenantId}' and status='ACTIVE';`,
          },
        },
      }),
    ])
      .then(async ([rolesResult, workerResult]) => {
        const dbUserRoles: any[] = rolesResult.data?.QueryGroupUserRoles ?? [];
        const wId: string = workerResult.data?.QueryWorker?.[0]?.worker_id ?? '';
        const roles = getUserRolesFromActiveContext(userContext);

        setWorkerId(wId);
        setActiveContextRoles(roles);
        setDbUserRoles(dbUserRoles);

        await loadProjects(roles, dbUserRoles, wId, userContext);
      })
      .catch(err => {
        console.error('Error loading QA projects:', err);
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext?.user?.userId, userContext?.activeContext?.tenantId]);

  useEffect(() => {
    if (!authToken) return;
    const all = [
      ...projectList.active,
      ...projectList.forReview,
      ...projectList.archived,
    ];
    if (all.length) fetchRubricInfo(all);
  }, [authToken, projectList, fetchRubricInfo]);

  // ─── Polling + event listeners ────────────────────────────────────────────

  const refreshableProjectsCount = useMemo(
    () =>
      projectList.active.length +
      projectList.forReview.length +
      projectList.archived.filter(p => p.availability !== false).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectList.active.length, projectList.forReview.length, projectList.archived.length],
  );

  // 30-second polling
  useEffect(() => {
    if (refreshableProjectsCount === 0) return;
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const cur = projectListRef.current;
      const all = [
        ...cur.active,
        ...cur.forReview,
        ...cur.archived.filter(p => p.availability !== false),
      ];
      if (all.length) {
        getProjectWorkItemCounts(all, true, activeContextRoles, workerId);
      }
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshableProjectsCount]);

  // WORK_ITEMS_UPDATED_EVENT — refresh counts with 500ms debounce
  useEffect(() => {
    const handler = () => {
      setTimeout(() => {
        const cur = projectListRef.current;
        const all = [
          ...cur.active,
          ...cur.forReview,
          ...cur.archived.filter(p => p.availability !== false),
        ];
        if (all.length) {
          getProjectWorkItemCounts(all, true, activeContextRoles, workerId);
        }
      }, 500);
    };
    window.addEventListener(WORK_ITEMS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(WORK_ITEMS_UPDATED_EVENT, handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContextRoles, workerId]);

  // QA_RULE_UPDATED_EVENT — re-fetch full project list (rubricVersionId may have changed)
  useEffect(() => {
    if (!workerId) return;
    const handler = () => loadProjects(activeContextRoles, dbUserRoles, workerId, userContext);
    window.addEventListener(QA_RULE_UPDATED_EVENT, handler);
    return () => window.removeEventListener(QA_RULE_UPDATED_EVENT, handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId, loadProjects]);

  // visibilitychange — refresh counts immediately on tab focus
  useEffect(() => {
    if (refreshableProjectsCount === 0) return;
    const handler = () => {
      if (document.visibilityState !== 'visible') return;
      const cur = projectListRef.current;
      const all = [
        ...cur.active,
        ...cur.forReview,
        ...cur.archived.filter(p => p.availability !== false),
      ];
      if (all.length) {
        getProjectWorkItemCounts(all, true, activeContextRoles, workerId);
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshableProjectsCount]);

  // ─── Filter by search term ────────────────────────────────────────────────

  const filterBySearch = useCallback(
    (projects: CollectionWithWorkItemCounts[]) => {
      if (!searchTerm) return projects;
      return projects.filter(p =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    },
    [searchTerm],
  );

  const filteredActive = useMemo(
    () => filterBySearch(projectList.active),
    [filterBySearch, projectList.active],
  );
  const filteredForReview = useMemo(
    () => filterBySearch(projectList.forReview),
    [filterBySearch, projectList.forReview],
  );
  const filteredArchived = useMemo(
    () => filterBySearch(projectList.archived),
    [filterBySearch, projectList.archived],
  );

  // ─── Render helpers ───────────────────────────────────────────────────────

  const renderProjectItem = (project: CollectionWithWorkItemCounts) => {
    const isSelected = selectedProjectId === project.collection_id;

    const rubricInfo = project.rubricVersionId
      ? rubricInfoCacheRef.current.get(project.rubricVersionId)
      : null;

    const showCounts = activeContextRoles.includes('cqa_manager') || activeContextRoles.includes('cqa_coder');
    const totalCount = project.workItemCounts
      ? (Object.entries(project.workItemCounts) as [WorkitemStatus, number][])
          .filter(([status]) => status !== WorkitemStatus.ASSIGNED_TO_ME)
          .reduce((sum, [, n]) => sum + n, 0)
      : 0;

    return (
      <Box key={project.collection_id}>
        <Box
          position="relative"
          px="12px"
          py="10px"
          mx="12px"
          mb="2px"
          borderRadius="3px"
          cursor="pointer"
          bg={isSelected ? SELECTED_BG : 'transparent'}
          _hover={{ bg: isSelected ? SELECTED_BG : HOVER_BG }}
          onClick={() => onProjectSelected(project, undefined)}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          data-testid="qa-sidebar-project-item"
          data-project-id={project.collection_id}
          data-project-name={project.name}
        >
          <Box flex={1} mr={isSelected ? '20px' : '8px'}>
            <Text
              fontSize="14px"
              lineHeight="16px"
              color={isSelected ? TEXT_SELECTED : TEXT_PRIMARY}
              fontWeight={isSelected ? 600 : 400}
              noOfLines={2}
            >
              {project.name}
            </Text>
            {rubricInfo && (
              <Text fontSize="11px" color={TEXT_LOW} mt="2px" noOfLines={1}>
                {rubricInfo.name} v{rubricInfo.version}
              </Text>
            )}
          </Box>

          {showCounts && (
            <Box flexShrink={0}>
              {countIsLoading && !project.workItemCounts ? (
                <Spinner size="xs" color={TEXT_LOW} />
              ) : (
                <Text fontSize="12px" color={TEXT_LOW} fontWeight={500}>
                  {totalCount}
                </Text>
              )}
            </Box>
          )}

          {isSelected && (
            <Text
              position="absolute"
              right="12px"
              top="50%"
              transform="translateY(-50%)"
              fontSize="12px"
              color={TEXT_LOW}
            >
              ›
            </Text>
          )}
        </Box>

      </Box>
    );
  };

  const renderSectionHeader = (
    title: string,
    isExpanded: boolean,
    onToggle: () => void,
    count: number,
  ) => (
    <Box
      px="12px"
      py="10px"
      cursor="pointer"
      onClick={onToggle}
      display="flex"
      alignItems="center"
      gap="8px"
      _hover={{ bg: HOVER_BG }}
    >
      <Text
        fontSize="11px"
        fontWeight={600}
        color={TEXT_LOW}
        letterSpacing="0.5px"
        textTransform="uppercase"
        flex={1}
      >
        {title} ({count})
      </Text>
      <Text fontSize="12px" color={TEXT_LOW}>
        {isExpanded ? '▾' : '▸'}
      </Text>
    </Box>
  );

  // ─── Loading state ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box p="16px">
        <Text fontSize="12px" color={TEXT_LOW}>
          Loading projects…
        </Text>
      </Box>
    );
  }

  const totalCount =
    projectList.active.length +
    projectList.forReview.length +
    projectList.archived.length;

  if (totalCount === 0) {
    return (
      <Box p="16px">
        <Text fontSize="12px" color={TEXT_LOW}>
          No projects found.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Home button for managers */}
      {isManager && onHomeClick && (
        <Box
          px="12px"
          py="10px"
          mx="12px"
          mb="4px"
          borderRadius="3px"
          cursor="pointer"
          bg={!selectedProjectId ? SELECTED_BG : 'transparent'}
          _hover={{ bg: !selectedProjectId ? SELECTED_BG : HOVER_BG }}
          onClick={onHomeClick}
          data-testid="manager-home-nav-button"
        >
          <Text
            fontSize="14px"
            color={!selectedProjectId ? TEXT_SELECTED : TEXT_PRIMARY}
            fontWeight={!selectedProjectId ? 600 : 400}
          >
            Project Health Dashboard
          </Text>
        </Box>
      )}

      {/* Active Projects */}
      {renderSectionHeader(
        'Active Projects',
        isActiveExpanded,
        () => {
          const next = !isActiveExpanded;
          setIsActiveExpanded(next);
          if (next) {
            setIsForReviewExpanded(false);
            setIsArchivedExpanded(false);
            sessionStorage.setItem(QA_EXPANDED_SECTION_KEY, 'active');
          } else {
            sessionStorage.removeItem(QA_EXPANDED_SECTION_KEY);
          }
        },
        filteredActive.length,
      )}
      {isActiveExpanded && filteredActive.map(renderProjectItem)}

      {/* Divider */}
      <Box borderBottom="1px solid rgba(255,255,255,0.08)" mx="12px" my="4px" />

      {/* For Review */}
      {renderSectionHeader(
        'For Review',
        isForReviewExpanded,
        () => {
          const next = !isForReviewExpanded;
          setIsForReviewExpanded(next);
          if (next) {
            setIsActiveExpanded(false);
            setIsArchivedExpanded(false);
            sessionStorage.setItem(QA_EXPANDED_SECTION_KEY, 'forReview');
          } else {
            sessionStorage.removeItem(QA_EXPANDED_SECTION_KEY);
          }
        },
        filteredForReview.length,
      )}
      {isForReviewExpanded && filteredForReview.map(renderProjectItem)}

      {/* Divider */}
      <Box borderBottom="1px solid rgba(255,255,255,0.08)" mx="12px" my="4px" />

      {/* Archived */}
      {renderSectionHeader(
        'Archived',
        isArchivedExpanded,
        () => {
          const next = !isArchivedExpanded;
          setIsArchivedExpanded(next);
          if (next) {
            setIsActiveExpanded(false);
            setIsForReviewExpanded(false);
            sessionStorage.setItem(QA_EXPANDED_SECTION_KEY, 'archived');
          } else {
            sessionStorage.removeItem(QA_EXPANDED_SECTION_KEY);
          }
        },
        filteredArchived.length,
      )}
      {isArchivedExpanded && filteredArchived.map(renderProjectItem)}
    </Box>
  );
};

export default QANavigationContent;
